if (!window.Eurus.loadedScript.has('invoice-request.js')) {
  window.Eurus.loadedScript.add('invoice-request.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      // Global store so the trigger link (cart page + minicart) can open the modal.
      Alpine.store('xInvoiceRequest', {
        open: false,
        openModal() {
          this.open = true;
          document.body.classList.add('overflow-hidden');
          document.dispatchEvent(new CustomEvent('eurus:invoice-request:open'));
        },
        closeModal() {
          this.open = false;
          document.body.classList.remove('overflow-hidden');
        },
      });

      Alpine.data('xInvoiceRequest', () => ({
        type: 'azienda',
        fields: {
          codice_fiscale: '',
          ragione_sociale: '',
          partita_iva: '',
          codice_sdi: '',
          pec: '',
        },
        errors: {},
        t: {},
        saved: false,
        saving: false,
        loadData() {
          const data = xParseJSON(this.$el.getAttribute('x-invoice-data') || '{}');
          this.t = xParseJSON(this.$el.getAttribute('x-invoice-labels') || '{}');

          this.type = data.type === 'privato' ? 'privato' : 'azienda';
          this.fields.codice_fiscale = data.codice_fiscale || '';
          this.fields.ragione_sociale = data.ragione_sociale || '';
          this.fields.partita_iva = data.partita_iva || '';
          this.fields.codice_sdi = data.codice_sdi || '';
          this.fields.pec = data.pec || '';
        },
        setType(value) {
          this.type = value;
          this.errors = {};
          this.saved = false;
        },
        // --- Validators -------------------------------------------------------
        isValidPartitaIva(value) {
          return /^[0-9]{11}$/.test(value);
        },
        isValidCodiceSdi(value) {
          return /^[A-Z0-9]{7}$/.test(value);
        },
        isValidPec(value) {
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        },
        isValidCodiceFiscale(value) {
          if (!/^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/.test(value)) {
            return false;
          }
          return value.charAt(15) === this._codiceFiscaleControlChar(value.slice(0, 15));
        },
        _codiceFiscaleControlChar(first15) {
          const odd = {
            '0': 1, '1': 0, '2': 5, '3': 7, '4': 9, '5': 13, '6': 15, '7': 17, '8': 19, '9': 21,
            'A': 1, 'B': 0, 'C': 5, 'D': 7, 'E': 9, 'F': 13, 'G': 15, 'H': 17, 'I': 19, 'J': 21,
            'K': 2, 'L': 4, 'M': 18, 'N': 20, 'O': 11, 'P': 3, 'Q': 6, 'R': 8, 'S': 12, 'T': 14,
            'U': 16, 'V': 10, 'W': 22, 'X': 25, 'Y': 24, 'Z': 23,
          };
          const even = {
            '0': 0, '1': 1, '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
            'A': 0, 'B': 1, 'C': 2, 'D': 3, 'E': 4, 'F': 5, 'G': 6, 'H': 7, 'I': 8, 'J': 9,
            'K': 10, 'L': 11, 'M': 12, 'N': 13, 'O': 14, 'P': 15, 'Q': 16, 'R': 17, 'S': 18,
            'T': 19, 'U': 20, 'V': 21, 'W': 22, 'X': 23, 'Y': 24, 'Z': 25,
          };
          let sum = 0;
          for (let i = 0; i < 15; i++) {
            const char = first15.charAt(i);
            // 1-indexed position: i+1. Odd position => i even.
            sum += (i % 2 === 0) ? odd[char] : even[char];
          }
          return String.fromCharCode(65 + (sum % 26));
        },
        // --- Field normalisation ---------------------------------------------
        upperCase(field) {
          this.fields[field] = (this.fields[field] || '').toUpperCase().replace(/\s+/g, '');
        },
        digitsOnly(field) {
          this.fields[field] = (this.fields[field] || '').replace(/\D+/g, '');
        },
        clearError(field) {
          if (this.errors[field]) {
            delete this.errors[field];
            this.errors = { ...this.errors };
          }
        },
        // --- Validation -------------------------------------------------------
        validate() {
          const errors = {};
          const f = this.fields;

          if (this.type === 'privato') {
            if (!f.codice_fiscale) {
              errors.codice_fiscale = this.t.required;
            } else if (!this.isValidCodiceFiscale(f.codice_fiscale)) {
              errors.codice_fiscale = this.t.invalid_codice_fiscale;
            }
          } else {
            if (!f.ragione_sociale) {
              errors.ragione_sociale = this.t.required;
            }
            if (!f.partita_iva) {
              errors.partita_iva = this.t.required;
            } else if (!this.isValidPartitaIva(f.partita_iva)) {
              errors.partita_iva = this.t.invalid_partita_iva;
            }
            // Codice fiscale optional for companies, validated when filled.
            if (f.codice_fiscale && !this.isValidCodiceFiscale(f.codice_fiscale)) {
              errors.codice_fiscale = this.t.invalid_codice_fiscale;
            }
            // Codice SDI e PEC sono facoltativi. SDI, se vuoto, viene salvato
            // come "0000000" (vedi save()). Entrambi validati solo se compilati.
            if (f.codice_sdi && !this.isValidCodiceSdi(f.codice_sdi)) {
              errors.codice_sdi = this.t.invalid_codice_sdi;
            }
            if (f.pec && !this.isValidPec(f.pec)) {
              errors.pec = this.t.invalid_pec;
            }
          }

          this.errors = errors;
          return Object.keys(errors).length === 0;
        },
        // --- Persistence ------------------------------------------------------
        save() {
          this.saved = false;
          if (!this.validate()) return;

          const f = this.fields;
          const isAzienda = this.type === 'azienda';
          const attributes = {
            attributes: {
              'Fattura - Tipo': isAzienda ? 'Azienda' : 'Privato',
              'Fattura - Codice fiscale': f.codice_fiscale || null,
              'Fattura - Ragione sociale': isAzienda ? f.ragione_sociale : null,
              'Fattura - Partita IVA': isAzienda ? f.partita_iva : null,
              'Fattura - Codice SDI': isAzienda ? (f.codice_sdi || '0000000') : null,
              'Fattura - PEC': isAzienda ? f.pec : null,
            },
          };

          this.saving = true;
          Alpine.store('xCartHelper').updateCart(attributes);
          this.saving = false;
          this.saved = true;

          setTimeout(() => {
            Alpine.store('xInvoiceRequest').closeModal();
          }, 800);
        },
      }));
    });
  });
}
